import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const TIP_OPTIONS = [
  { label: 'No Tip', percent: 0 },
  { label: '10%', percent: 0.10 },
  { label: '15%', percent: 0.15 },
  { label: '20%', percent: 0.20 },
];

export default function TipPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTip, setSelectedTip] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await fetch(`${API}/api/payments/booking/${bookingId}`);
        if (!res.ok) throw new Error('Booking not found');
        const data = await res.json();
        setBooking(data);
        if (data.booking?.tip_submitted) setSubmitted(true);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  const getTipAmount = () => {
    if (selectedTip === 'custom') return parseFloat(customTip) || 0;
    if (selectedTip !== null) {
      const price = parseFloat(booking?.booking?.price || 0);
      return Math.round(price * TIP_OPTIONS[selectedTip].percent * 100) / 100;
    }
    return 0;
  };

  const handleSubmit = async () => {
    const tipAmount = getTipAmount();
    try {
      const res = await fetch(`${API}/api/payments/set-tip/${bookingId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tip_amount: tipAmount })
      });
      if (!res.ok) throw new Error('Failed to submit tip');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-sm max-w-md w-full text-center">
          <p className="text-red-500">{error || 'Booking not found'}</p>
        </div>
      </div>
    );
  }

  const b = booking.booking;
  const price = parseFloat(b.price || 0);
  const deposit = booking.deposit_amount;
  const remaining = booking.remaining_balance;
  const tipAmount = getTipAmount();

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm max-w-md w-full text-center" data-testid="tip-submitted">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-4">
            Your tip preference has been saved. Your guide {b.guide_name} appreciates your generosity!
          </p>
          <p className="text-sm text-gray-400">
            The remaining balance will be charged to your card on file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-[#2640be] px-6 py-8 text-center">
          <h1 className="text-white text-2xl font-bold tracking-wider">We.Tour</h1>
          <p className="text-blue-200 text-sm mt-1">Complete Your Tour Payment</p>
        </div>

        <div className="p-6">
          {/* Tour Summary */}
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Tour Summary</h2>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Guide</span>
                <span className="font-medium">{b.guide_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">School</span>
                <span className="font-medium">{b.school_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tour Type</span>
                <span className="font-medium">{b.tour_type}</span>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Payment Breakdown</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Tour Price</span>
                <span>${price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Deposit Paid</span>
                <span>-${deposit.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-medium">
                <span>Remaining Balance</span>
                <span>${remaining.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Tip Selection */}
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Add a Tip for {b.guide_name}</h2>
            <p className="text-sm text-gray-500 mb-3">Your guide worked hard to give you a great experience!</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {TIP_OPTIONS.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`tip-option-${opt.percent}`}
                  onClick={() => { setSelectedTip(i); setCustomTip(''); }}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    selectedTip === i
                      ? 'border-[#2640be] bg-blue-50 text-[#2640be]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-gray-500">${(price * opt.percent).toFixed(2)}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => { setSelectedTip('custom'); }}
              className={`w-full p-3 rounded-xl border-2 text-center transition-all ${
                selectedTip === 'custom'
                  ? 'border-[#2640be] bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-sm font-medium">Custom Amount</span>
            </button>
            {selectedTip === 'custom' && (
              <div className="mt-2 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-4 py-2 border rounded-lg text-sm"
                  data-testid="custom-tip-input"
                />
              </div>
            )}
          </div>

          {/* Total */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Remaining Balance</span>
              <span>${remaining.toFixed(2)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Tip</span>
                <span>${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-bold text-lg">
              <span>Total to Charge</span>
              <span>${(remaining + tipAmount).toFixed(2)}</span>
            </div>
          </div>

          {/* Submit */}
          <button
            data-testid="submit-tip-btn"
            onClick={handleSubmit}
            disabled={selectedTip === null}
            className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
              selectedTip !== null
                ? 'bg-[#2640be] hover:bg-[#1e35a0]'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Confirm Tip Selection
          </button>
          <p className="text-xs text-gray-400 text-center mt-3">
            The total will be charged to your card on file by the We.Tour team.
          </p>
        </div>
      </div>
    </div>
  );
}
